/**
 * `W5-T12` §4.10 — back from Checkout (`?ask=paid`), the job page re-reads the job every three seconds
 * for up to a minute while it is still a draft: Checkout redirects as soon as the payment succeeds,
 * and `checkout.session.completed` publishes the job a few seconds later.
 *
 * `W5-T01`'s watch, with one difference: the job is read through `ensureQueryData` (the page's other
 * poll must not re-read it), so each tick refetches the job itself before the loader runs again. A
 * tick is skipped while the tab is hidden or the router is busy, and still counts, so the minute is a
 * minute. Returns `false` once the minute has run out, so the page can say the payment is slow.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigation, useRevalidator } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../shared/query.js';

export const ASK_WATCH_EVERY_MS = 3_000;
export const ASK_WATCH_FOR_MS = 60_000;

export function useAskReturnWatch(active: boolean, jobId: string): boolean {
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();
  const navigation = useNavigation();

  const busy = useRef(false);
  busy.current = revalidator.state !== 'idle' || navigation.state !== 'idle';
  const revalidate = useRef(revalidator.revalidate);
  revalidate.current = revalidator.revalidate;
  const ticksLeft = useRef(Math.floor(ASK_WATCH_FOR_MS / ASK_WATCH_EVERY_MS));
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!active || ticksLeft.current <= 0) return undefined;
    const timer = setInterval(() => {
      ticksLeft.current -= 1;
      if (ticksLeft.current <= 0) {
        clearInterval(timer);
        setOpen(false);
      }
      if (busy.current || document.visibilityState !== 'visible') return;
      void queryClient
        .invalidateQueries({ queryKey: queryKeys.job(jobId), exact: true, refetchType: 'all' })
        .then(() => revalidate.current());
    }, ASK_WATCH_EVERY_MS);
    return () => clearInterval(timer);
  }, [active, jobId, queryClient]);

  return open;
}
